import {Fragment, useEffect, useRef, useState} from "react";
import {useAssetsImage} from  '~/pages/context/AssetsImageContext';
import {Send, Paperclip} from 'lucide-react'
import classNames from "classnames/bind";
import styles from "./LiveChat.module.scss";
import {useParams} from "react-router-dom";
import ChatMessageGroup from '~/pages/administrator/AIChatbotPage/SupportCaseAdmin/component/ChatMessageGroup';
import * as signalR from "@microsoft/signalr";
import {toast} from "react-toastify";
import SupportCaseAPI from "~/api/supportCaseApi";
const cx = classNames.bind(styles);
const apiUrl = process.env.REACT_APP_API_URL;
function LiveChat(){
    const { id } = useParams();
    const {getImageUrlById} = useAssetsImage();
    const logo = getImageUrlById(39);
    useEffect(() => {
        document.title = "Support Live Chat";
    }, []);

    const [messageGroup, setMessageGroup] = useState([]);
    const [messageInput, setMessageInput] = useState("");

    const [connection, setConnection] = useState(null);
    const [assignedAdmin, setAssignedAdmin] = useState(null);

    const [isFocused, setIsFocused] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const chatEndRef = useRef(null);
    const [toggleScrollEndChat, setToggleScrollEndChat] = useState(false);

    useEffect(() => {
        SupportCaseAPI.getCurrentSupporter()
            .then((res) => {
                if(res.data?.CaseSessionId > 0) {
                    setAssignedAdmin(res.data);
                    SupportCaseAPI.getCaseChatBySession(res.data.CaseSessionId)
                        .then((r) => {
                            setMessageGroup(groupMessagesBySenderSequence(r.data));
                        })
                        .catch(() => {
                            setAssignedAdmin({
                                SupporterId: ""
                            });
                            setTimeout(function () {
                                window.close();
                            }, 5000)
                        })
                }
            })
            .catch(() => {
                setAssignedAdmin({
                    SupporterId: ""
                });
                setTimeout(function () {
                    window.close();
                }, 5000)
            })
    }, [])

    useEffect(() => {
        if (isFocused && hasUnread) {
            // noinspection JSUnresolvedVariable
            connection.invoke(
                "MarkMessageAsRead",
                assignedAdmin.CurrentSessionId,
                assignedAdmin.CaseSessionId,
                false,
                ""
            );
            setHasUnread(false);
        }
    }, [isFocused, hasUnread]);

    useEffect(() => {
        const connect = new signalR.HubConnectionBuilder()
            .withUrl(`${apiUrl}/chatHub`, {
                withCredentials: true
            })
            .withAutomaticReconnect()
            .build();

        connect.start()
            .then(() => {
                setConnection(connect);
            })
            .catch(() => {});


        connect.on("UserReceiveMessage", async (chatContent) => {
            handleMessageChange(true, chatContent, "");
            setToggleScrollEndChat(prev => !prev);
            if (document.hidden) {
                setHasUnread(true);
                return;
            }
            if (isFocused) {
                // noinspection JSUnresolvedVariable
                await connection.invoke(
                    "MarkMessageAsRead",
                    assignedAdmin.CurrentSessionId,
                    assignedAdmin.CaseSessionId,
                    false,
                    ""
                );
            } else {
                setHasUnread(true);
            }
        });

        connect.on("MessageStatusUpdate", (tempId, status) => {
            updateMessageGroupStatus(tempId, status);
        });

        connect.on("UserReceiveAccepted", (isAccept) => {
            if(isAccept){
                SupportCaseAPI.getCurrentSupporter()
                    .then((res) => {
                        setAssignedAdmin(res.data);
                    })
                    .catch(() => {
                        setAssignedAdmin({
                            SupporterId: ""
                        });
                        setTimeout(function () {
                            window.close();
                        }, 5000)
                    })
            }
            else{
                setAssignedAdmin({
                    SupporterId: ""
                });
                setTimeout(function () {
                    window.close();
                }, 5000)
            }
        });

        connect.on("MarkAsRead", () => {
            setMessageGroup(prev => {
                const newK = [...prev];
                const lastIndex = newK.length - 1;

                const updatedGroup = newK[lastIndex].map(item =>
                    item.IsRead === false ? { ...item, IsRead: true } : item
                );
                newK[lastIndex] = updatedGroup;
                return newK;
            });
        });

        connect.on("ChatEndedByAdmin", () => {
            setAssignedAdmin({
                SupporterId: ""
            });
            setTimeout(function () {
                window.close();
            }, 5000)
        });

        return () => {
            connect.stop();
        };
    }, []);

    const updateMessageGroupStatus = (tempId, status) => {
        setMessageGroup(prev =>
            prev.map(group => {
                let changed = false;
                const updatedGroup = group.map(item => {
                    if (item.TempId === tempId) {
                        changed = true;
                        return { ...item, Status: status };
                    }
                    return item;
                });
                return changed ? updatedGroup : group;
            })
        );
    };

    const handleMessageChange = (isSupporter, chatContent, tempId = "") => {
        const currentRole = isSupporter ? "Supporter" : "Customer";
        const newMessage = {
            SupporterOrCustomer: currentRole,
            ChatContent: chatContent,
            CreatedDate: new Date().toISOString(),
            TempId: tempId,
            Status: !isSupporter ? "sending" : "",
            IsRead: false
        };

        setMessageGroup(prevMessages => {
            const updatedMessages = [...prevMessages];
            const lastGroup = updatedMessages[updatedMessages.length - 1];
            if (lastGroup && (lastGroup[0].SupporterOrCustomer === currentRole)) {
                updatedMessages[updatedMessages.length - 1] = [...lastGroup, newMessage];
            } else {
                updatedMessages.push([{ ...newMessage }]);
            }
            return updatedMessages;
        });
    }

    const groupMessagesBySenderSequence = (messages) => {
        const grouped = [];
        let currentGroup = [];
        messages.forEach((msg) => {
            if (
                currentGroup.length === 0 ||
                currentGroup[currentGroup.length - 1].SupporterOrCustomer === msg.SupporterOrCustomer
            ) {
                currentGroup.push(msg);
            } else {
                grouped.push(currentGroup);
                currentGroup = [msg];
            }
        });

        if (currentGroup.length > 0) grouped.push(currentGroup);
        return grouped;
    };

    const handleKeyDown = async (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            await handleSendMessage();
        }
    };

    const handleSendMessage = async () => {
        if (!messageInput.trim()) return;
        const tempId = "temp_" + Date.now();
        handleMessageChange(false, messageInput, tempId);
        const newMessage = {
            SupporterOrCustomer: "Customer",
            ChatContent: messageInput,
            CaseSessionId: assignedAdmin.CaseSessionId
        };
        // noinspection JSUnresolvedVariable
        await connection.invoke("UserReply", assignedAdmin.CurrentSessionId, tempId, newMessage);
        setMessageInput("");
        setToggleScrollEndChat(prev => !prev);
    };

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [toggleScrollEndChat]);

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file || !assignedAdmin) return;

        const validImageTypes = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
        const maxSize = 5 * 1024 * 1024;

        if (!validImageTypes.includes(file.type)) {
            toast.error(<span className="fs-9 fw-bold">Only image files are allowed (JPG, PNG, GIF, WEBP).</span>)
            e.target.value = "";
            return;
        }

        if (file.size > maxSize) {
            toast.error(<span className="fs-9 fw-bold">The file size must be smaller than 5MB.</span>)
            e.target.value = "";
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("CaseSessionId", assignedAdmin.CaseSessionId);
        formData.append("SupportCaseId", assignedAdmin.SupportCaseId);

        SupportCaseAPI.insertCaseFile(formData)
            .then((res) => {
                // noinspection JSUnresolvedVariable
                res.data?.Success ?
                    toast.success(<span className="fs-9 fw-bold">Your file has been uploaded successfully.</span>)
                    : toast.error(<span className="fs-9 fw-bold">An error occurred while updating your file.</span>)
            })
            .catch(() => {
                toast.error(<span className="fs-9 fw-bold">An error occurred while updating your file.</span>)
            })

        e.target.value = "";
    };

    // noinspection JSUnresolvedVariable
    return (
        <Fragment>
            <div>
                <div className="d-flex align-items-center gap-2 justify-content-between">
                    <img width="50" src={logo} alt="logo"/>
                    <div className="me-2 fs-9">CaseID: <strong>{id}</strong></div>
                </div>
                <div className="warning-chat text-center">
                    <span className="fs-9">Warning - We value your private. Please do not include any Personally identifiable Information (PII) through the chat.</span>
                </div>
                {
                    !assignedAdmin &&
                    (
                        <div className="chat-body pt-3 ps-2 pe-2">
                            <div className="waiting-chat text-center">
                                <span className="fs-9 fw-bold">Hi.</span>
                                <div className={cx("jawn", "mt-6 mb-6")}></div>
                                <span className="fs-9 fw-bold">An associate will be with you shortly...</span>
                            </div>
                        </div>
                    )
                }
                {
                    assignedAdmin && assignedAdmin.SupporterId === "" &&
                    (
                        <div className="chat-body pt-3 ps-2 pe-2">
                            <div className="waiting-chat text-center">
                                <span className="fs-9 fw-bold">Hi.</span>
                                <div className={cx("jawn", "mt-6 mb-6")}></div>
                                <p className="fs-9 fw-bold text-danger">The admin is currently unavailable or has declined the chat request. Please try again later.</p>
                                <p className="fs-9 fw-bold text-danger">Window chat will auto close after 5s.</p>
                            </div>
                        </div>
                    )
                }
                {
                    assignedAdmin && assignedAdmin.SupporterId !== "" &&
                    (
                        <div className="pt-3 d-flex flex-column">
                            <div className="chat-header border mb-2">
                                <div className=" d-flex align-items-center px-1 py-1 text-body" data-discover="true">
                                    <div className="avatar avatar-xl status-online">
                                        <img alt="avatar" className="rounded-circle" src={assignedAdmin.SupporterAvatarUrl}/>
                                    </div>
                                    <p className="mb-0 ms-3 text-body-emphasis fw-semibold">{assignedAdmin.SupporterName}</p>
                                </div>
                            </div>
                            <div className={cx("chat-body","scrollbar mx-1 px-2 d-flex flex-column gap-2 ")}>
                                {messageGroup.map((group, index) => {
                                    const isReceiver = group[0].SupporterOrCustomer === "Supporter";
                                    const time = new Date(group[group.length - 1].CreatedDate).toLocaleString();
                                    // noinspection JSUnresolvedVariable
                                    return (
                                        <ChatMessageGroup
                                            key={index}
                                            group={group}
                                            isReceiver={isReceiver}
                                            time={time}
                                            avatar={assignedAdmin.SupporterAvatarUrl}
                                            isLast={messageGroup.length - 1 === index}
                                        />
                                    );
                                })}
                                <div ref={chatEndRef}></div>
                            </div>
                            <div className={cx("chat-footer", "px-2 bg-body-emphasis border mx-3 mb-2 position-relative")}>
                                <textarea
                                    ref={textareaRef}
                                    placeholder="Type message here..."
                                    className="resize-none outline-none w-100 fs-8-1 mt-2 fw-semibold border-0 scrollbar"
                                    value={messageInput}
                                    onChange={(e) => setMessageInput(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    onFocus={() => setIsFocused(true)}
                                    onBlur={() => setIsFocused(false)}
                                />
                                <div className={cx("position-absolute", "chat-action")}>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        id="fileUpload"
                                        style={{ display: "none" }}
                                        onChange={handleFileSelect}
                                    />
                                    <Paperclip size={15} className="cursor-pointer me-3"
                                               onClick={() => fileInputRef.current.click()}
                                    />
                                    <Send size={15} className="cursor-pointer"/>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>
        </Fragment>
    )
}

export default LiveChat;